import { enTheme, ITheme, IThemeCustomizer, IThemeCustomizerModel, IThemeCustomizerOptions } from 'interfaces/theme';
import * as _ from 'lodash';
import * as uuid from 'node-uuid';
import * as rxjs from 'rxjs';
import rxjsOperators, { take } from 'rxjs-operators';
import authService from 'services/auth';

import { GetDefaultTheme, GetThemeOptions } from '../adapters/ThemeAdapter';
import apiService, { ApiService } from './api';
import { themeService } from './themeService';

export class ThemeCustomizerService {
  private openForm$: rxjs.BehaviorSubject<boolean>;
  private openCustomizer$: rxjs.BehaviorSubject<boolean>;
  private selectedOption$: rxjs.BehaviorSubject<IThemeCustomizerOptions>;
  private themeId$: rxjs.BehaviorSubject<number>;
  private theme$: rxjs.BehaviorSubject<IThemeCustomizer>;
  private themeCustomizer$: rxjs.BehaviorSubject<IThemeCustomizerModel>;

  private state = <IThemeCustomizerModel>{
    themeCustomizer: {}
  };

  constructor(private api: ApiService) {
    this.openCustomizer$ = new rxjs.BehaviorSubject(false);
    this.openForm$ = new rxjs.BehaviorSubject(false);
    this.selectedOption$ = new rxjs.BehaviorSubject(null);
    this.theme$ = new rxjs.BehaviorSubject(null);
    this.themeCustomizer$ = new rxjs.BehaviorSubject(GetDefaultTheme(enTheme.PADRAO));
    this.themeId$ = new rxjs.BehaviorSubject(enTheme.PADRAO);

    this.state = GetDefaultTheme(enTheme.PADRAO);
  }

  init() {
    this.themeId$.pipe(take(1)).subscribe(themeId => {
      const theme = GetDefaultTheme(themeId);
      this.state = theme;
      this.themeCustomizer$.next(JSON.parse(JSON.stringify(this.state)));
    });
  }

  public openForm(): void {
    this.openForm$.next(true);
  }

  public closeForm(): void {
    this.openForm$.next(false);
    this.selectedOption$.next(null);
  }

  public shouldOpenForm(): rxjs.Observable<boolean> {
    return this.openForm$.asObservable();
  }

  public openCustomizer(): void {
    this.openCustomizer$.next(true);
  }

  public closeCustomizer(): void {
    this.openCustomizer$.next(false);
  }

  public setSelectedOption(index: number): void {
    this.theme$.pipe(rxjsOperators.logError()).subscribe(theme => {
      const option = theme.options[index];
      this.selectedOption$.next(option);
    });
  }

  public getSelectedOption(): rxjs.Observable<IThemeCustomizerOptions> {
    return this.selectedOption$.asObservable();
  }

  public shouldOpenCustomizer(): rxjs.Observable<boolean> {
    return this.openCustomizer$.asObservable();
  }

  public editTheme(theme: IThemeCustomizerModel): void {
    this.themeId$.next(Number(theme.template));
    this.setData(theme);
  }

  public setTheme(themeId: enTheme): void {
    this.themeId$.next(themeId);
    this.themeCustomizer$.next(GetDefaultTheme(themeId));
    this.setData({ template: themeId.toString() });
  }

  public getThemeId(): rxjs.Observable<enTheme> {
    return this.themeId$.asObservable();
  }

  public getTheme(): rxjs.Observable<IThemeCustomizer> {
    return this.theme$.asObservable();
  }

  public getThemeParameters(): rxjs.Observable<IThemeCustomizer> {
    return this.getThemeId().pipe(
      rxjsOperators.distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      rxjsOperators.switchMap(themeId => rxjs.of(GetThemeOptions(themeId))),
      rxjsOperators.tap(theme => this.theme$.next(theme))
    );
  }

  public setData(obj: Partial<IThemeCustomizerModel> = {}): void {
    const data = this.state;

    this.state = { ...data, ...obj };

    this.themeCustomizer$.next(JSON.parse(JSON.stringify(this.state)));
  }

  public getData(): rxjs.Observable<IThemeCustomizerModel> {
    return this.themeCustomizer$.pipe(
      rxjsOperators.distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }

  public themePostMessage(): rxjs.Observable<any> {
    return this.themeCustomizer$.pipe(
      rxjsOperators.logError(),
      rxjsOperators.switchMap(themeCustomizer => {
        return rxjs.of(this.normalizeThemeMessage(themeCustomizer));
      })
    );
  }

  public saveCustomization(): void {
    this.getData()
      .pipe(
        rxjsOperators.take(1),
        rxjsOperators.combineLatest(themeService.getSelectedTheme().pipe(rxjsOperators.take(1)), authService.getUser()),
        rxjsOperators.filter(([customization]) => !!customization),
        rxjsOperators.switchMap(
          ([customization, selectedTheme, user]): rxjs.Observable<ITheme> => {
            let newTheme: ITheme = JSON.parse(JSON.stringify(selectedTheme));

            if (!newTheme) {
              newTheme = {
                id: uuid.v4().replace(/-/g, ''),
                userId: user.id,
                imgDesktop: '',
                imgMobile: '',
                name: customization.themeName,
                config: {},
                linkedProducts: [],
                campaigns: []
              };
            }

            const type = this.normalizeThemeType(Number(customization.template));

            newTheme.name = customization.themeName;
            newTheme.type = type;

            _.merge(newTheme.config, customization);
            const dataSave: { theme: ITheme; publishProductId?: number } = {
              theme: newTheme
            };

            return this.api.post('/theme/save', dataSave);
          }
        ),
        rxjsOperators.combineLatest(themeService.getSelectedTheme().pipe(rxjsOperators.take(1))),
        rxjsOperators.logError(),
        rxjsOperators.loader()
      )
      .subscribe(([theme, selectedTheme]) => {
        if (selectedTheme) {
          themeService.setDataTheme(theme);
        }

        if (!selectedTheme) {
          themeService.addTheme(theme);
        }

        this.closeCustomizer();
      });
  }

  public normalizeThemeId(theme: string): enTheme {
    const themeName = theme.trim().toUpperCase();

    if (themeName === 'HOTSITE') {
      return enTheme.HOTSITE;
    }

    if (themeName === 'WIDGET') {
      return enTheme.WIDGET;
    }

    return enTheme.PADRAO;
  }

  public normalizeThemeType(themeId: enTheme): string {
    if (themeId === enTheme.HOTSITE) {
      return 'hotsite';
    }

    if (themeId === enTheme.WIDGET) {
      return 'widget';
    }

    return 'padrão';
  }

  private formatColor(color: string): string {
    return color.charAt(0) === '#' ? color.slice(1) : color;
  }

  private normalizeThemeMessage(theme: IThemeCustomizerModel): IThemeCustomizerModel {
    let normalizeTheme = theme;
    normalizeTheme.labels.font.color = this.formatColor(normalizeTheme.labels.font.color);
    normalizeTheme.inputs.bgcolor = this.formatColor(normalizeTheme.inputs.bgcolor);
    normalizeTheme.inputs.font.color = this.formatColor(normalizeTheme.inputs.font.color);
    normalizeTheme.inputs.border.color = this.formatColor(normalizeTheme.inputs.border.color);
    normalizeTheme.background.color = this.formatColor(normalizeTheme.background.color);
    normalizeTheme.buttons.bgcolor = this.formatColor(normalizeTheme.buttons.bgcolor);
    normalizeTheme.buttons.bgcolorhover = this.formatColor(normalizeTheme.buttons.bgcolorhover);
    normalizeTheme.buttons.font.color = this.formatColor(normalizeTheme.buttons.font.color);
    normalizeTheme.header.bgcolor = this.formatColor(normalizeTheme.header.bgcolor);
    normalizeTheme.header.font.color = this.formatColor(normalizeTheme.header.font.color);
    normalizeTheme.footer.font.color = this.formatColor(normalizeTheme.footer.font.color);
    normalizeTheme.languageBar.font.color = this.formatColor(normalizeTheme.languageBar.font.color);
    normalizeTheme.toastMessages.bgColor = this.formatColor(normalizeTheme.toastMessages.bgColor);
    normalizeTheme.toastMessages.font.color = this.formatColor(normalizeTheme.toastMessages.font.color);

    return normalizeTheme;
  }
}

const themeCustomizerService = new ThemeCustomizerService(apiService);
export default themeCustomizerService;
